import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Sparkles,
  Loader2,
  Plus,
  FileText,
  ChevronRight,
  Check,
  AlertCircle,
} from 'lucide-react';
import { suggestClauses, ClauseSuggestion } from '../services/claudeAI';

interface AIClauseSuggestionsProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: string;
  jurisdiction: string;
  existingClauses: string[];
  onInsertClause: (clauseContent: string, clauseName: string) => void;
}

export function AIClauseSuggestions({
  isOpen,
  onClose,
  documentType,
  jurisdiction,
  existingClauses,
  onInsertClause,
}: AIClauseSuggestionsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ClauseSuggestion[]>([]);
  const [selectedClause, setSelectedClause] = useState<ClauseSuggestion | null>(null);
  const [error, setError] = useState('');
  const [insertedClauses, setInsertedClauses] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && suggestions.length === 0) {
      fetchSuggestions();
    }
  }, [isOpen]);

  const fetchSuggestions = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await suggestClauses(
        documentType,
        jurisdiction,
        existingClauses
      );
      setSuggestions(result);
    } catch (err) {
      console.error('Suggestion error:', err);
      setError('Failed to get suggestions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsert = (clause: ClauseSuggestion) => {
    onInsertClause(clause.content, clause.name);
    setInsertedClauses((prev) => new Set([...prev, clause.name]));
    setSelectedClause(null);
  };

  const handleClose = () => {
    setSuggestions([]);
    setSelectedClause(null);
    setInsertedClauses(new Set());
    setError('');
    onClose();
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'warranties':
        return 'bg-blue-500/20 text-blue-400';
      case 'boilerplate':
        return 'bg-slate-500/20 text-slate-400';
      case 'signatures':
        return 'bg-purple-500/20 text-purple-400';
      case 'definitions':
        return 'bg-cyan-500/20 text-cyan-400';
      case 'provisions':
        return 'bg-amber-500/20 text-amber-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-3xl max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">AI Clause Suggestions</h2>
                  <p className="text-sm text-slate-400">
                    Smart recommendations for your {documentType}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex h-[calc(85vh-180px)]">
              {/* Suggestions List */}
              <div className="w-1/2 border-r border-slate-700 overflow-y-auto p-4">
                {isLoading && (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 text-pink-500 animate-spin mx-auto mb-3" />
                    <p className="text-slate-400">Analyzing document context...</p>
                  </div>
                )}

                {error && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    {error}
                  </div>
                )}

                {!isLoading && suggestions.length === 0 && !error && (
                  <div className="text-center py-12 text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No additional clauses suggested</p>
                  </div>
                )}

                <div className="space-y-2">
                  {suggestions.map((clause, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      onClick={() => setSelectedClause(clause)}
                      className={`w-full text-left p-4 rounded-lg border transition-colors ${
                        selectedClause?.name === clause.name
                          ? 'bg-pink-500/20 border-pink-500/50'
                          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-white truncate">
                              {clause.name}
                            </h4>
                            {insertedClauses.has(clause.name) && (
                              <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                            )}
                          </div>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(
                              clause.category
                            )}`}
                          >
                            {clause.category}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">
                            {Math.round(clause.relevanceScore * 100)}%
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {suggestions.length > 0 && (
                  <button
                    onClick={fetchSuggestions}
                    disabled={isLoading}
                    className="mt-4 w-full py-2 text-sm text-slate-400 hover:text-white transition-colors"
                  >
                    Refresh Suggestions
                  </button>
                )}
              </div>

              {/* Clause Preview */}
              <div className="w-1/2 p-4 overflow-y-auto">
                {selectedClause ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium text-white mb-2">
                        {selectedClause.name}
                      </h3>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(
                          selectedClause.category
                        )}`}
                      >
                        {selectedClause.category}
                      </span>
                    </div>

                    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                      <p className="text-sm text-slate-400 mb-2">Why this clause?</p>
                      <p className="text-slate-300">{selectedClause.explanation}</p>
                    </div>

                    <div>
                      <p className="text-sm text-slate-400 mb-2">Clause Content</p>
                      <pre className="p-4 bg-slate-800 rounded-lg text-sm text-slate-300 whitespace-pre-wrap">
                        {selectedClause.content}
                      </pre>
                    </div>

                    <button
                      onClick={() => handleInsert(selectedClause)}
                      disabled={insertedClauses.has(selectedClause.name)}
                      className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        insertedClauses.has(selectedClause.name)
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white'
                      }`}
                    >
                      {insertedClauses.has(selectedClause.name) ? (
                        <>
                          <Check className="w-4 h-4" />
                          Inserted
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Insert Clause
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Select a clause to preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-xs text-slate-500">
                {insertedClauses.size} clause{insertedClauses.size !== 1 ? 's' : ''} inserted
              </p>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default AIClauseSuggestions;
